import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import { sortClubLifeFeedPosts } from "../../../utils/clubLifeFeedSort";
import { useClientAthlete } from "../../../context/ClientAthleteContext";
import { formatDateRu, formatTimeRu } from "../../../utils/appointmentLabels";
import { formatExpiryAlert, givenNameFromFullName, sameDay, toDateKey } from "../../../utils/clientAppUtils";
import {
  pickNearestTrainingSession,
  pickUpcomingSessions,
  sessionsOnDay,
  isCompetitionSession,
} from "../../../utils/clientScheduleUtils";
import {
  pollNeedsResponse,
  pollCalendarPath,
  isAttendancePoll,
  surveyPollType,
  sessionAccentStyle,
  trainerName,
  groupName,
  normalizeSurveyRows,
} from "../../../utils/clientSessionUtils";
import ClientSurveyFeed from "./ClientSurveyFeed";
import AttendancePollButtons from "./AttendancePollButtons";
import SessionStatusBadge from "./SessionStatusBadge";
import ClubLifePostCard from "../common/ClubLifePostCard";
import ClubLifeRepostModal from "../common/ClubLifeRepostModal";
import ClientPostManageModals from "./ClientPostManageModals";
import ClubLifeComposer from "./ClubLifeComposer";
import ClientChildSwitcher from "./ClientChildSwitcher";
import NotificationsDropdown from "../common/NotificationsDropdown";
import { useClubLifeInteractions } from "../../../hooks/useClubLifeInteractions";
import { useClientPostManage } from "../../../hooks/useClientPostManage";
import s from "./clientApp.module.css";
import { NAV_ICONS, IconBell, IconSettings } from "../../ui/icons";
import useClubPortalBranding from "../../../hooks/useClubPortalBranding";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import { Avatar, useToast } from "../../ui";
import { useClientRefresh } from "../../../hooks/useClientRefresh";
import { usePlanAccess } from "../../../hooks/usePlanAccess";
import CompetitionApplySheet from "./CompetitionApplySheet";
import CompetitionDetailSheet from "./CompetitionDetailSheet";
import {
  competitionAssemblyLabel,
  competitionVenueLine,
  competitionUiState,
  clubEventTag,
} from "../../../utils/competitionUtils";

const CalendarIcon = NAV_ICONS.schedule;

function calendarDayPath(startDatetime) {
  return `/app/calendar?date=${toDateKey(startDatetime)}`;
}

function todayLabel() {
  return new Date().toLocaleDateString("ru-RU", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function homeVisitsRemaining(membership, stats) {
  const mv = stats?.membership_visits;
  if (mv?.is_visit_based && mv.remaining != null) return mv.remaining;
  if (membership?.visits_remaining != null) return membership.visits_remaining;
  return null;
}

function homeVisitsRemainingDisplay(membership, stats) {
  const remaining = homeVisitsRemaining(membership, stats);
  if (remaining != null) {
    return { value: remaining, label: "Осталось", hint: "занятий" };
  }
  if (stats?.membership_visits?.is_visit_based) {
    return { value: 0, label: "Осталось", hint: "занятий" };
  }
  if (membership) {
    return { value: "∞", label: "Абонемент", hint: "безлимит" };
  }
  return { value: "—", label: "Осталось", hint: "занятий" };
}

function homeGroupRankStat(stats) {
  const rank = stats?.group_attendance_rank;
  if (!rank?.group_name) {
    return { value: "—", label: "В группе", hint: "по явке" };
  }
  if (rank.rank != null && rank.total > 0) {
    const hintParts = [`из ${rank.total}`];
    if (rank.pct != null) hintParts.push(`${rank.pct}%`);
    if (rank.streak >= 2) hintParts.push(`серия ${rank.streak}`);
    return { value: rank.rank, label: "Место", hint: hintParts.join(" · ") };
  }
  if (rank.pct != null) {
    return { value: `${rank.pct}%`, label: "Явка", hint: "в группе" };
  }
  return { value: "—", label: "В группе", hint: "нет данных" };
}

export default function ClientHome() {
  const toast = useToast();
  const { profile, tenant } = useSelector((state) => state.auth);
  const {
    isParentMode,
    activeAthlete,
    athleteQueryParam,
    activeAthleteId,
    athletes,
  } = useClientAthlete();
  const plan = usePlanAccess();
  const showChildSwitcher = isParentMode && athletes.length > 1;
  const [dash, setDash] = useState(null);
  const [surveySections, setSurveySections] = useState({
    attendance: [],
    feedback: [],
    custom: [],
  });
  const [polls, setPolls] = useState([]);
  const [posts, setPosts] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rsvpPhase, setRsvpPhase] = useState("choose");
  const [applyCompetition, setApplyCompetition] = useState(null);
  const [viewCompetition, setViewCompetition] = useState(null);
  const [applying, setApplying] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyBadge, setNotifyBadge] = useState(0);
  const [repostTarget, setRepostTarget] = useState(null);
  const [repostBusy, setRepostBusy] = useState(false);
  const notifyBtnRef = useRef(null);

  const {
    commentBusy,
    openComments,
    commentsByPost,
    toggleLike,
    loadComments,
    addComment,
  } = useClubLifeInteractions(setPosts);
  const postManage = useClientPostManage(setPosts);

  const handleRepostRequest = (post) => {
    setRepostTarget(post);
  };

  const confirmRepost = async (comment) => {
    if (!repostTarget || repostBusy) return;
    setRepostBusy(true);
    try {
      await clinicAPI.repostToWall({
        post_id: repostTarget.id,
        comment: comment || undefined,
      });
      toast.success("Запись добавлена на вашу стену");
      setRepostTarget(null);
    } catch {
      toast.error("Не удалось поделиться");
    } finally {
      setRepostBusy(false);
    }
  };

  const load = useCallback(async () => {
    const q = athleteQueryParam;
    const [dashRes, pollRes, feedRes, schedRes, compRes, notifRes] =
      await Promise.allSettled([
      clinicAPI.getMyClientDashboard(q),
      clinicAPI.getMyTrainingPolls(q),
      plan.clubLife
        ? clinicAPI.getClubLifeFeed(q)
        : Promise.resolve({ posts: [] }),
      clinicAPI.getMySchedule(q),
      plan.competitions
        ? clinicAPI.getMyCompetitions(q)
        : Promise.resolve({ competitions: [] }),
      clinicAPI.getMyNotifications(q),
    ]);

    if (dashRes.status === "fulfilled") {
      setDash(dashRes.value);
    } else {
      setDash(null);
    }

    if (pollRes.status === "fulfilled") {
      const data = pollRes.value;
      setSurveySections(
        data.sections || {
          attendance: [],
          feedback: [],
          custom: [],
        },
      );
      setPolls(normalizeSurveyRows(data.polls || []));
    } else {
      setSurveySections({ attendance: [], feedback: [], custom: [] });
      setPolls([]);
    }

    if (feedRes.status === "fulfilled") {
      setPosts(sortClubLifeFeedPosts(feedRes.value.posts || []).slice(0, 3));
    } else {
      setPosts([]);
    }

    if (schedRes.status === "fulfilled") {
      setSchedule(schedRes.value.sessions || []);
    } else {
      setSchedule([]);
    }

    if (compRes.status === "fulfilled") {
      setCompetitions(compRes.value.competitions || []);
    } else {
      setCompetitions([]);
    }

    if (notifRes.status === "fulfilled") {
      setNotifyBadge(notifRes.value.badge_count ?? notifRes.value.unread_count ?? 0);
    } else if (pollRes.status === "fulfilled") {
      setNotifyBadge(
        (pollRes.value.polls || []).filter(pollNeedsResponse).length,
      );
    } else {
      setNotifyBadge(0);
    }

    setLoading(false);
  }, [athleteQueryParam, plan.clubLife, plan.competitions]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load, activeAthleteId]);

  useClientRefresh(load);

  useEffect(() => {
    const refreshNotifyBadge = () => {
      clinicAPI
        .getMyNotifications(athleteQueryParam)
        .then((data) => setNotifyBadge(data.badge_count ?? data.unread_count ?? 0))
        .catch(() => {});
    };
    window.addEventListener("baysport:chat-activity", refreshNotifyBadge);
    window.addEventListener("baysport:poll-activity", refreshNotifyBadge);
    return () => {
      window.removeEventListener("baysport:chat-activity", refreshNotifyBadge);
      window.removeEventListener("baysport:poll-activity", refreshNotifyBadge);
    };
  }, [athleteQueryParam]);

  const membership = dash?.membership;
  const stats = dash?.stats;
  const card = dash?.profile?.patient_card;
  const groups = dash?.groups || [];

  const todaySessions = useMemo(
    () => sessionsOnDay(schedule, new Date()),
    [schedule],
  );

  const focusTraining = useMemo(
    () => pickNearestTrainingSession(schedule),
    [schedule],
  );

  const upcomingStrip = useMemo(() => {
    return pickUpcomingSessions(schedule, 6)
      .filter(
        (sess) =>
          !isCompetitionSession(sess) && sess.id !== focusTraining?.id,
      )
      .slice(0, 3);
  }, [schedule, focusTraining]);

  const pendingPolls = useMemo(() => polls.filter(pollNeedsResponse), [polls]);
  const pendingAttendancePolls = useMemo(
    () => pendingPolls.filter(isAttendancePoll),
    [pendingPolls],
  );
  const displaySurveySections = useMemo(() => {
    const pending = polls.filter(pollNeedsResponse);
    const pick = (type) =>
      pending.filter((row) => surveyPollType(row) === type);
    return {
      attendance:
        surveySections.attendance?.filter(pollNeedsResponse).length > 0
          ? surveySections.attendance.filter(pollNeedsResponse)
          : pick("attendance"),
      feedback:
        surveySections.feedback?.filter(pollNeedsResponse).length > 0
          ? surveySections.feedback.filter(pollNeedsResponse)
          : pick("feedback"),
      custom:
        surveySections.custom?.filter(pollNeedsResponse).length > 0
          ? surveySections.custom.filter(pollNeedsResponse)
          : pick("custom"),
    };
  }, [polls, surveySections]);
  const surveyFeedVisible =
    (displaySurveySections.attendance?.length || 0) > 0 ||
    (displaySurveySections.feedback?.length || 0) > 0 ||
    (displaySurveySections.custom?.length || 0) > 0;
  const notifyBadgeCount = notifyBadge;
  const firstName = givenNameFromFullName(
    isParentMode && activeAthlete ? activeAthlete.full_name : profile?.full_name,
  );

  const alerts = useMemo(() => {
    const list = [];
    const med = formatExpiryAlert(card?.medical_clearance_expires_at, "Допуск");
    if (med) list.push(med);
    const memb = formatExpiryAlert(membership?.ends_at, "Абонемент");
    if (
      memb &&
      (membership?.status === "active" || membership?.status === "expired")
    ) {
      list.push({
        ...memb,
        href: "/app/profile/account/club",
        action: "Продлить",
      });
    } else if (memb) {
      list.push(memb);
    }
    if (membership?.status === "active" && !memb) {
      const left = homeVisitsRemaining(membership, stats);
      const total =
        stats?.membership_visits?.total ?? membership?.plan?.visits_count ?? null;
      const visitsLow =
        left != null &&
        (left <= 3 || (total != null && total > 0 && left / total <= 0.25));
      if (visitsLow) {
        list.push({
          tone: "warn",
          text:
            left === 0
              ? "Занятия по абонементу закончились"
              : left === 1
                ? "Осталось 1 занятие по абонементу"
                : `Осталось ${left} занятий по абонементу`,
          href: "/app/profile/account/club",
          action: "Продлить",
        });
      }
    }
    const pendingPay =
      membership?.status === "pending"
        ? membership
        : dash?.pending_membership?.status === "pending"
          ? dash.pending_membership
          : (dash?.memberships || []).find((m) => m?.status === "pending");
    if (pendingPay) {
      const planName = pendingPay.plan?.name || "абонемент";
      list.push({
        tone: "warn",
        text: `Ожидается оплата: ${planName}`,
        href: "/app/profile/account/club",
        action: "Оплатить",
      });
    } else {
      const debtMem =
        membership && Number(membership.amount_due) > 0
          ? membership
          : (dash?.memberships || []).find(
              (m) =>
                (m?.status === "active" || m?.status === "frozen") &&
                Number(m?.amount_due) > 0,
            );
      if (debtMem) {
        const sum = Math.round(Number(debtMem.amount_due));
        list.push({
          tone: "warn",
          text: `Абонемент действует, к оплате: ${sum.toLocaleString("ru-RU")} ₽`,
          href: "/app/profile/account/club",
          action: "Оплатить",
        });
      }
    }
    const fees = dash?.session_fees_due || [];
    if (fees.length === 1) {
      const sum = fees[0].session_fee_amount;
      list.push({
        tone: "warn",
        text:
          sum != null
            ? `К оплате за занятие: ${Math.round(Number(sum)).toLocaleString("ru-RU")} ₽`
            : "Есть неоплаченное занятие",
        href: "/app/profile/account/club",
        action: "Оплатить",
      });
    } else if (fees.length > 1) {
      list.push({
        tone: "warn",
        text: `Неоплаченных занятий: ${fees.length}`,
        href: "/app/profile/account/club",
        action: "Оплатить",
      });
    }
    return list;
  }, [card, membership, stats, dash?.pending_membership, dash?.memberships, dash?.session_fees_due]);

  const trainerBrand = dash?.trainer_branding;
  const { theme: heroTheme, shellStyle } = useClubPortalBranding(
    "ca",
    trainerBrand,
    false,
  );

  if (loading) {
    return <div className={s.home} aria-hidden />;
  }

  const isUpcoming =
    focusTraining && new Date(focusTraining.start_datetime) >= new Date();

  const showRsvp =
    focusTraining &&
    isUpcoming &&
    focusTraining.status !== "cancelled" &&
    focusTraining.session_kind !== "event";

  const monthVisits = stats?.attendance_month?.present;
  const visitsStat = homeVisitsRemainingDisplay(membership, stats);
  const groupRankStat = homeGroupRankStat(stats);

  return (
    <>
    <ClubLifeRepostModal
      post={repostTarget}
      open={Boolean(repostTarget)}
      busy={repostBusy}
      onClose={() => !repostBusy && setRepostTarget(null)}
      onConfirm={confirmRepost}
    />
    <ClientPostManageModals {...postManage} />
    <div className={s.home} style={shellStyle}>
      <div className={s.homeHeroBlock}>
        <section
          className={`${s.homeHero} ${
            heroTheme.coverUrl ? `${s.homeHeroHasCover} ${s.homeHeroCover}` : s.homeHeroGradient
          }`}
        >
          <div
            className={s.homeHeroBg}
            key={heroTheme.coverUrl || "solid"}
            style={
              heroTheme.coverUrl
                ? { backgroundImage: `url(${heroTheme.coverUrl})` }
                : { backgroundColor: heroTheme.accent }
            }
            aria-hidden
          />
          {trainerBrand?.photo_url ? (
            <div className={s.homeHeroTrainer}>
              <Avatar
                name={trainerBrand.full_name}
                src={mediaUrl(trainerBrand.photo_url)}
                size="sm"
              />
              <span>{trainerBrand.full_name}</span>
            </div>
          ) : null}
          <div className={s.homeHeroTop}>
            <div className={s.homeHeroMeta}>
              <p className={s.homeHeroDate}>{todayLabel()}</p>
              <h1 className={s.homeHeroTitle}>
                {firstName
                  ? isParentMode
                    ? firstName
                    : `Привет, ${firstName}`
                  : "Добро пожаловать"}
              </h1>
              <p className={s.homeHeroClub}>{tenant?.name || "Ваш клуб"}</p>
              {groups.length > 0 ? (
                <div className={s.homeHeroTags}>
                  {groups.slice(0, 2).map((g) => (
                    <span key={g.id} className={s.homeHeroTag}>
                      {g.name}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            <div className={s.homeHeroTopActions}>
              <button
                ref={notifyBtnRef}
                type="button"
                className={s.homeHeroIconBtn}
                aria-label={
                  notifyBadgeCount > 0
                    ? `Уведомления: ${notifyBadgeCount}`
                    : "Уведомления"
                }
                aria-expanded={notifyOpen}
                onClick={() => setNotifyOpen((v) => !v)}
              >
                <IconBell width={20} height={20} />
                {notifyBadgeCount > 0 ? (
                  <span className={s.homeHeroIconBadge}>
                    {notifyBadgeCount > 9 ? "9+" : notifyBadgeCount}
                  </span>
                ) : null}
              </button>
              <Link
                to="/app/profile/account"
                className={s.homeHeroIconBtn}
                aria-label="Настройки"
              >
                <IconSettings width={20} height={20} />
              </Link>
            </div>
          </div>
        </section>

        <div className={s.homeStats}>
          <div className={s.homeStat}>
            <span className={s.homeStatVal}>{visitsStat.value}</span>
            <span className={s.homeStatLbl}>{visitsStat.label}</span>
            {visitsStat.hint ? (
              <span className={s.homeStatHint}>{visitsStat.hint}</span>
            ) : null}
          </div>
          <div className={s.homeStatDivider} aria-hidden />
          <Link to="/app/stats" className={`${s.homeStat} ${s.homeStatLink}`}>
            <span className={s.homeStatVal}>{groupRankStat.value}</span>
            <span className={s.homeStatLbl}>{groupRankStat.label}</span>
            {groupRankStat.hint ? (
              <span className={s.homeStatHint}>{groupRankStat.hint}</span>
            ) : null}
          </Link>
          <div className={s.homeStatDivider} aria-hidden />
          <div className={s.homeStat}>
            <span className={s.homeStatVal}>
              {monthVisits ?? "—"}
            </span>
            <span className={s.homeStatLbl}>Посещений</span>
            <span className={s.homeStatHint}>за месяц</span>
          </div>
        </div>
        {showChildSwitcher ? (
          <ClientChildSwitcher variant="home" />
        ) : null}
        {stats?.group_attendance_rank?.rank ? (
          <p className={s.homeStatsFoot}>
            <Link to="/app/stats" className={s.homeStatsFootLink}>
              Открыть рейтинг группы →
            </Link>
          </p>
        ) : null}
      </div>

      <div className={s.homeBody}>
        <ClientSurveyFeed sections={displaySurveySections} onDone={load} />

        {pendingAttendancePolls.length > 0 && !surveyFeedVisible ? (
          <Link
            to={
              pendingAttendancePolls[0]
                ? pollCalendarPath(pendingAttendancePolls[0])
                : "/app/calendar"
            }
            className={s.homePollBanner}
          >
            <span className={s.homePollBannerIcon}>!</span>
            <div>
              <strong>
                {pendingAttendancePolls.length}{" "}
                {pendingAttendancePolls.length === 1 ? "опрос" : "опроса"} о явке
              </strong>
              <p>Подтвердите участие в календаре</p>
            </div>
            <span className={s.homePollBannerArrow}>→</span>
          </Link>
        ) : null}

        {alerts.map((a) =>
          a.href ? (
            <Link
              key={a.text}
              to={a.href}
              className={`${s.homeAlert} ${
                a.tone === "danger" ? s.homeAlertDanger : s.homeAlertWarn
              } ${s.homeAlertLink}`}
            >
              <span>{a.text}</span>
              {a.action ? <span className={s.homeAlertAction}>{a.action} →</span> : null}
            </Link>
          ) : (
            <div
              key={a.text}
              className={`${s.homeAlert} ${
                a.tone === "danger" ? s.homeAlertDanger : s.homeAlertWarn
              }`}
            >
              {a.text}
            </div>
          ),
        )}

        <section className={`${s.homeSection} ${s.homeCard} ${s.homeScheduleCard}`}>
          <div className={s.homeCardHead}>
            <h2 className={s.homeSectionTitle}>Ближайшие занятия</h2>
            <Link to="/app/calendar" className={s.homeSectionLink}>
              Календарь
            </Link>
          </div>

          {focusTraining ? (
            <article
              className={`${s.homeSession} ${s.homeSessionFeatured}`}
              style={sessionAccentStyle(focusTraining, "--slot-accent")}
            >
              <Link
                to={calendarDayPath(focusTraining.start_datetime)}
                className={s.homeSessionBody}
              >
                <div className={s.homeSessionTop}>
                  <div>
                    <span className={s.homeSessionTime}>
                      {formatTimeRu(focusTraining.start_datetime)}
                    </span>
                    <span className={s.homeSessionDate}>
                      {formatDateRu(focusTraining.start_datetime, {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      })}
                    </span>
                  </div>
                  <SessionStatusBadge appointment={focusTraining} />
                </div>
                <h3 className={s.homeSessionName}>
                  {groupName(focusTraining, groups)}
                </h3>
                <p className={s.homeSessionMeta}>
                  {[trainerName(focusTraining), focusTraining.branch?.name]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </Link>

              {showRsvp ? (
                <div
                  className={`${s.homeSessionRsvp} ${
                    rsvpPhase === "reason" ? s.homeSessionRsvpReason : ""
                  } ${rsvpPhase === "answered" ? s.homeSessionRsvpAnswered : ""}`}
                >
                  <AttendancePollButtons
                    session={focusTraining}
                    allowDirect
                    variant="sheet"
                    onPhaseChange={setRsvpPhase}
                    onDone={load}
                  />
                </div>
              ) : null}
            </article>
          ) : (
            <div className={s.homeRest}>
              <div className={s.homeRestIcon} aria-hidden>
                <CalendarIcon />
              </div>
              <h3>Ближайших тренировок нет</h3>
              <p>
                Когда тренер добавит занятия в вашу группу, они появятся здесь и
                в календаре
              </p>
              <Link to="/app/calendar" className={s.homeRestBtn}>
                Смотреть календарь
              </Link>
            </div>
          )}

          {upcomingStrip.length > 0 ? (
            <div className={s.homeUpcomingStrip}>
              <p className={s.homeUpcomingStripLabel}>Ещё в расписании</p>
              <div className={s.homeUpcomingStripRow}>
                {upcomingStrip.map((sess) => (
                  <Link
                    key={sess.id}
                    to={calendarDayPath(sess.start_datetime)}
                    className={s.homeUpcomingChip}
                  >
                    <span className={s.homeUpcomingChipDay}>
                      {formatDateRu(sess.start_datetime, {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      })}
                    </span>
                    <span className={s.homeUpcomingChipTime}>
                      {formatTimeRu(sess.start_datetime)}
                    </span>
                    <strong className={s.homeUpcomingChipName}>
                      {groupName(sess, groups)}
                    </strong>
                  </Link>
                ))}
              </div>
            </div>
          ) : null}
        </section>

        {plan.competitions ? (
        <section className={`${s.homeSection} ${s.homeCard} ${s.homeCompetitions}`}>
          <div className={s.homeCardHead}>
            <h2 className={s.homeSectionTitle}>Соревнования</h2>
            <Link to="/app/calendar" className={s.homeSectionLink}>
              Календарь
            </Link>
          </div>

          {competitions.length === 0 ? (
            <div className={s.homeCompetitionEmpty}>
              <span className={s.homeCompetitionEmptyIcon} aria-hidden>
                🏆
              </span>
              <p>Ближайших соревнований пока нет</p>
              <span>Когда клуб объявит старт — увидите здесь и сможете подать заявку</span>
            </div>
          ) : (
            <ul className={s.homeCompetitionList}>
              {competitions.map((c) => {
                const ui = competitionUiState(c);
                const calDate = toDateKey(c.start_datetime);
                return (
                  <li key={c.id} className={s.homeCompetitionRow}>
                    <button
                      type="button"
                      className={s.homeCompetitionRowMainBtn}
                      onClick={() => setViewCompetition(c)}
                    >
                      {c.cover_image_url ? (
                        <span
                          className={s.homeCompetitionCover}
                          style={{
                            backgroundImage: `url(${mediaUrl(c.cover_image_url)})`,
                          }}
                          aria-hidden
                        />
                      ) : (
                        <span className={s.homeCompetitionIcon} aria-hidden>
                          🏆
                        </span>
                      )}
                      <div className={s.homeCompetitionRowText}>
                        <strong>{c.title || "Соревнование"}</strong>
                        <p>
                          {formatDateRu(c.start_datetime, {
                            weekday: "short",
                            day: "numeric",
                            month: "long",
                          })}
                          {c.start_datetime
                            ? ` · ${formatTimeRu(c.start_datetime)}`
                            : ""}
                        </p>
                        {competitionAssemblyLabel(c) ? (
                          <span className={s.homeCompetitionHint}>
                            {competitionAssemblyLabel(c)}
                          </span>
                        ) : null}
                        {competitionVenueLine(c) ? (
                          <span className={s.homeCompetitionHint}>
                            {competitionVenueLine(c)}
                          </span>
                        ) : null}
                        {ui.label ? (
                          <span
                            className={`${s.homeCompetitionBadge} ${s[`homeCompetitionBadge_${ui.badge}`] || ""}`}
                          >
                            {ui.label}
                          </span>
                        ) : null}
                      </div>
                    </button>
                    <div className={s.homeCompetitionRowActions}>
                      {ui.canApply ? (
                        <button
                          type="button"
                          className={s.homeCompetitionApply}
                          onClick={() => setApplyCompetition(c)}
                        >
                          Подать заявку
                        </button>
                      ) : (
                        <Link
                          to={`/app/calendar?date=${calDate}`}
                          className={s.homeCompetitionCalLink}
                        >
                          В календарь
                        </Link>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
        ) : null}

        {plan.clubLife ? (
        <section className={`${s.homeSection} ${s.homeCard}`}>
          <div className={s.homeCardHead}>
            <h2 className={s.homeSectionTitle}>Клуб</h2>
            <Link to="/app/life" className={s.homeSectionLink}>
              Лента
            </Link>
          </div>
          <div className={s.homeComposerWrap}>
            <ClubLifeComposer compact embedded onPublished={load} />
          </div>
          {posts.length > 0 ? (
            <div className={s.homeFeed}>
              {posts.map((post) => (
                <ClubLifePostCard
                  key={post.id}
                  post={post}
                  mode="client"
                  currentUserId={profile?.id}
                  onOpenMenu={postManage.openMenu}
                  onLike={toggleLike}
                  onLoadComments={loadComments}
                  commentsOpen={openComments === post.id}
                  comments={commentsByPost[post.id] || []}
                  onComment={addComment}
                  commentBusy={commentBusy}
                  onRepost={handleRepostRequest}
                />
              ))}
            </div>
          ) : null}
        </section>
        ) : null}
      </div>

      {viewCompetition ? (
        <CompetitionDetailSheet
          competition={viewCompetition}
          ui={competitionUiState(viewCompetition)}
          onClose={() => setViewCompetition(null)}
          onApply={(comp) => {
            setViewCompetition(null);
            setApplyCompetition(comp);
          }}
        />
      ) : null}

      {applyCompetition ? (
        <CompetitionApplySheet
          competition={applyCompetition}
          onClose={() => setApplyCompetition(null)}
          submitting={applying}
          onSubmit={async (data) => {
            setApplying(true);
            try {
              await clinicAPI.applyToCompetition(
                applyCompetition.id,
                data,
                athleteQueryParam,
              );
              toast.success("Заявка отправлена");
              setApplyCompetition(null);
              load();
            } catch (e) {
              toast.error(e.response?.data?.error || "Ошибка");
            } finally {
              setApplying(false);
            }
          }}
        />
      ) : null}
    </div>

    <NotificationsDropdown
      open={notifyOpen}
      onClose={() => setNotifyOpen(false)}
      anchorRef={notifyBtnRef}
      allPath="/app/notifications"
      portal="client"
      athleteId={activeAthlete?.id || ""}
      onMarkedRead={() => {
        clinicAPI
          .getMyNotifications(athleteQueryParam)
          .then((data) => setNotifyBadge(data.badge_count ?? data.pending_polls_count ?? 0))
          .catch(() => setNotifyBadge((c) => c));
      }}
    />
    </>
  );
}
